<template>
  <Modal
    :show="showModal"
    size="sm"
    dialog-max-width="400px"
    :blur-backdrop="false"
    :backdropOpacity="0.5"
    :show-close-button="false"
    @close="closeModal"
    headerVariant="blue"
  >
    <template #header>
      <div class="meeting-header__text">
        <h2 class="meeting-header__title">Edit Meeting Title</h2>
        <p class="meeting-header__subtitle">Update the meeting title</p>
      </div>
      <button type="button" class="meeting-header__close" @click="closeModal" aria-label="Close">
        <Icon icon="mdi:close" width="18" height="18" />
      </button>
    </template>
    <template #default>
      <div class="modal-body-content">
        <Input
          v-model="formData.title"
          label="Title"
          type="text"
          placeholder="Enter meeting name"
          :error="submitError"
          leading-icon="mdi:format-title"
          required
        />
      </div>
    </template>

    <template #footer>
      <div class="modal-footer-actions">
        <Button variant="secondary" @click="closeModal"> Cancel </Button>
        <Button
          variant="primary"
          :loading="isPending"
          :disabled="!titleTrimmed"
          @click="handleSubmit"
        >
          Save
        </Button>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { Modal, Button, Input, Icon } from "@/components/ui";
import { useEditMeeting } from "@/composables/meetings";

const props = defineProps<{
  selectedMeeting: { id: string; title: string } | null;
}>();

const emit = defineEmits<{
  closeEditMeetingModal: [];
  refreshMeeting: [meeting: { id: string; title: string } | Record<string, unknown>];
}>();

const editMeetingMutation = useEditMeeting();

const showModal = ref(true);
const submitError = ref("");

const formData = ref({
  id: null as string | null,
  title: "" as string,
});

const titleTrimmed = computed(() => (formData.value.title ?? "").trim().length > 0);
const isPending = computed(() => editMeetingMutation.isPending.value);

const setValues = () => {
  if (!props.selectedMeeting) return;
  formData.value.id = props.selectedMeeting.id;
  formData.value.title = props.selectedMeeting.title ?? "";
};

const closeModal = () => {
  emit("closeEditMeetingModal");
};

const handleSubmit = async () => {
  const title = (formData.value.title ?? "").trim();
  if (!title) {
    submitError.value = "Title is required";
    return;
  }
  if (!formData.value.id) return;

  submitError.value = "";
  try {
    const result = await editMeetingMutation.mutateAsync({
      id: formData.value.id,
      title,
    } as any);

    emit("refreshMeeting", result ?? { id: formData.value.id, title });
    closeModal();
  } catch (error) {
    console.error("Error updating meeting title:", error);
    submitError.value = "Failed to update title. Please try again.";
  }
};

onMounted(() => {
  setValues();
});
</script>

<style scoped>
.modal-body-content {
  padding: 24px;
}

.modal-footer-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  width: 100%;
}

.meeting-header__text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.meeting-header__title {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #fff;
  letter-spacing: -0.01em;
}
.meeting-header__subtitle {
  margin: 0;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.85);
}
.meeting-header__close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  cursor: pointer;
  color: #fff;
  transition: all 0.15s ease;
  flex-shrink: 0;
}
.meeting-header__close:hover {
  background: rgba(255, 255, 255, 0.35);
}
</style>
